import React from "react";

var spacingKeys = {
  m: ["margin"],
  mt: ["marginTop"],
  mr: ["marginRight"],
  mb: ["marginBottom"],
  ml: ["marginLeft"],
  mx: ["marginLeft", "marginRight"],
  my: ["marginTop", "marginBottom"],
  p: ["padding"],
  pt: ["paddingTop"],
  pr: ["paddingRight"],
  pb: ["paddingBottom"],
  pl: ["paddingLeft"],
  px: ["paddingLeft", "paddingRight"],
  py: ["paddingTop", "paddingBottom"],
};

var propAliases = {
  bgcolor: "backgroundColor",
  borderColor: "borderColor",
};

function isPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function normalizeUnit(key, value) {
  if (value == null) return value;
  if (typeof value !== "number") return value;

  if (key === "opacity" || key === "fontWeight" || key === "zIndex" || key === "lineHeight" || key === "flex" || key === "order") {
    return value;
  }

  if (key === "gap" || spacingKeys[key]) {
    return value * 8 + "px";
  }

  return value;
}

function readResponsiveValue(value) {
  if (!isPlainObject(value)) return value;
  if (value.xs != null) return value.xs;
  if (value.sm != null) return value.sm;
  if (value.md != null) return value.md;
  var keys = Object.keys(value);
  return keys.length ? value[keys[0]] : undefined;
}

export function sxToStyle(sx) {
  var result = {};
  var source = Array.isArray(sx) ? Object.assign.apply(Object, [{}].concat(sx.filter(Boolean))) : sx;

  if (!source || typeof source !== "object") return result;

  Object.keys(source).forEach(function (key) {
    if (key.charAt(0) === "&" || key.charAt(0) === "@" || key.indexOf(":") !== -1) return;

    var value = readResponsiveValue(source[key]);
    if (value == null) return;

    if (spacingKeys[key]) {
      spacingKeys[key].forEach(function (styleKey) {
        result[styleKey] = normalizeUnit(key, value);
      });
      return;
    }

    var styleKey = propAliases[key] || key;
    result[styleKey] = normalizeUnit(key, value);
  });

  return result;
}

function resolveClassName(baseClassName, className, gapClassName) {
  return [baseClassName, gapClassName, className].filter(Boolean).join(" ");
}

function gapClassFromStyle(style) {
  if (!style || style.gap == null) return "";
  var rawGap = style.gap;
  var gap = typeof rawGap === "number" ? rawGap * 8 + "px" : String(rawGap);
  var safeGap = gap.replace(/[^0-9a-zA-Z_-]/g, "-");
  return "legacy-ui-gap legacy-ui-gap-" + safeGap;
}

function mergeStyles(style, sx) {
  var sxStyle = sxToStyle(sx);
  var merged = Object.assign({}, sxStyle, style || {});
  return merged;
}

function createPrimitive(defaultElement, baseClassName) {
  return React.forwardRef(function LegacyPrimitive(props, ref) {
    var children = props.children;
    var className = props.className;
    var component = props.component;
    var sx = props.sx;
    var style = props.style;
    var rest = Object.assign({}, props);
    delete rest.children;
    delete rest.className;
    delete rest.component;
    delete rest.sx;
    delete rest.style;

    var Element = component || defaultElement;
    var mergedStyle = mergeStyles(style, sx);
    var gapClassName = gapClassFromStyle(mergedStyle);
    if (gapClassName) {
      delete mergedStyle.gap;
      delete mergedStyle.rowGap;
      delete mergedStyle.columnGap;
    }

    return (
      <Element ref={ref} className={resolveClassName(baseClassName, className, gapClassName)} style={mergedStyle} {...rest}>
        {children}
      </Element>
    );
  });
}

export var Box = createPrimitive("div", "legacy-ui-box");
export var Paper = createPrimitive("div", "legacy-ui-paper");
export var Card = createPrimitive("div", "legacy-ui-card");
export var CardContent = createPrimitive("div", "legacy-ui-card-content");
export var CardActions = createPrimitive("div", "legacy-ui-card-actions");
export var Typography = createPrimitive("p", "legacy-ui-typography");
export var Divider = createPrimitive("hr", "legacy-ui-divider");

export function CardHeader(props) {
  var title = props.title;
  var subheader = props.subheader;
  var avatar = props.avatar;
  var action = props.action;
  var children = props.children;
  var className = props.className;
  var sx = props.sx;
  var style = props.style;
  var rest = Object.assign({}, props);
  delete rest.title;
  delete rest.subheader;
  delete rest.avatar;
  delete rest.action;
  delete rest.children;
  delete rest.className;
  delete rest.sx;
  delete rest.style;

  return (
    <div className={resolveClassName("legacy-ui-card-header", className)} style={mergeStyles(style, sx)} {...rest}>
      {avatar ? <div className="legacy-ui-card-header-avatar">{avatar}</div> : null}
      <div className="legacy-ui-card-header-text">
        {title ? <div className="legacy-ui-card-header-title">{title}</div> : null}
        {subheader ? <div className="legacy-ui-card-header-subheader">{subheader}</div> : null}
        {children}
      </div>
      {action ? <div className="legacy-ui-card-header-action">{action}</div> : null}
    </div>
  );
}

export var Link = React.forwardRef(function LegacyLink(props, ref) {
  return <Box ref={ref} component="a" className={resolveClassName("legacy-ui-link", props.className)} {...props} />;
});

export var ButtonBase = React.forwardRef(function LegacyButtonBase(props, ref) {
  var children = props.children;
  var className = props.className;
  var component = props.component;
  var sx = props.sx;
  var style = props.style;
  var disabled = props.disabled;
  var rest = Object.assign({}, props);
  delete rest.children;
  delete rest.className;
  delete rest.component;
  delete rest.sx;
  delete rest.style;
  delete rest.disabled;

  var Element = component || "button";
  var mergedStyle = mergeStyles(style, sx);

  return (
    <Element
      ref={ref}
      className={resolveClassName("legacy-ui-button-base", className)}
      style={mergedStyle}
      disabled={Element === "button" ? disabled : undefined}
      aria-disabled={disabled ? "true" : undefined}
      {...rest}
    >
      {children}
    </Element>
  );
});

export var Button = React.forwardRef(function LegacyButton(props, ref) {
  return <ButtonBase ref={ref} {...props} className={resolveClassName("legacy-ui-button", props.className)} />;
});

export function Drawer(props) {
  var anchor = props.anchor || "right";
  var open = !!props.open;
  var onClose = props.onClose;
  var children = props.children;
  var className = props.className;
  var sx = props.sx;
  var style = props.style;

  if (!open) return null;

  return (
    <div className="legacy-ui-drawer-root" role="presentation">
      <button className="legacy-ui-drawer-backdrop" type="button" aria-label="close" onClick={onClose} />
      <div className={resolveClassName("legacy-ui-drawer legacy-ui-drawer-" + anchor, className)} style={mergeStyles(style, sx)}>
        {children}
      </div>
    </div>
  );
}

export function Popover(props) {
  var open = !!props.open;
  var onClose = props.onClose;
  var children = props.children;
  var className = props.className;
  var sx = props.sx;
  var style = props.style;

  if (!open) return null;

  return (
    <div className="legacy-ui-popover-root" role="presentation">
      <button className="legacy-ui-popover-backdrop" type="button" aria-label="close" onClick={onClose} />
      <div className={resolveClassName("legacy-ui-popover", className)} style={mergeStyles(style, sx)}>
        {children}
      </div>
    </div>
  );
}

export function Checkbox(props) {
  var checked = !!props.checked;
  var className = props.className;
  var sx = props.sx;
  var style = props.style;
  var rest = Object.assign({}, props);
  delete rest.className;
  delete rest.sx;
  delete rest.style;

  return (
    <span className={resolveClassName("legacy-ui-checkbox", checked ? "legacy-ui-checkbox-checked " + className : className)} style={mergeStyles(style, sx)}>
      <input type="checkbox" checked={checked} {...rest} />
      <span className="legacy-ui-checkbox-box" />
    </span>
  );
}

export function CircularProgress(props) {
  var size = props.size || 40;
  var sx = props.sx;
  var style = props.style;
  var className = props.className;
  var mergedStyle = mergeStyles(Object.assign({ width: size, height: size }, style || {}), sx);
  return <span className={resolveClassName("legacy-ui-circular-progress", className)} style={mergedStyle} role="progressbar" />;
}

export function LinearProgress(props) {
  var sx = props.sx;
  var style = props.style;
  var className = props.className;
  return (
    <div className={resolveClassName("legacy-ui-linear-progress", className)} style={mergeStyles(style, sx)} role="progressbar">
      <span />
    </div>
  );
}

export function Skeleton(props) {
  var variant = props.variant || "text";
  var className = props.className;
  var sx = props.sx;
  var style = props.style;
  var rest = Object.assign({}, props);
  delete rest.variant;
  delete rest.className;
  delete rest.sx;
  delete rest.style;

  return <span className={resolveClassName("legacy-ui-skeleton legacy-ui-skeleton-" + variant, className)} style={mergeStyles(style, sx)} {...rest} />;
}

export function Fade(props) {
  if (props.in === false) return null;
  return <>{props.children}</>;
}

export function Collapse(props) {
  if (props.in === false) return null;
  return <div className="legacy-ui-collapse">{props.children}</div>;
}

export function CssBaseline() {
  return null;
}

export function ThemeProvider(props) {
  return <>{props.children}</>;
}

export function createTheme(theme) {
  return theme || {};
}

var LegacyMaterialCompat = {
  Box: Box,
  Button: Button,
  ButtonBase: ButtonBase,
  Card: Card,
  CardActions: CardActions,
  CardContent: CardContent,
  CardHeader: CardHeader,
  Checkbox: Checkbox,
  Collapse: Collapse,
  CircularProgress: CircularProgress,
  CssBaseline: CssBaseline,
  Divider: Divider,
  Drawer: Drawer,
  Fade: Fade,
  LinearProgress: LinearProgress,
  Link: Link,
  Paper: Paper,
  Popover: Popover,
  Skeleton: Skeleton,
  ThemeProvider: ThemeProvider,
  Typography: Typography,
  createTheme: createTheme,
};

export default LegacyMaterialCompat;
